import type { QuickSearchResponse, QuickSearchResult } from '@/types/searchTypes'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GRID_COLUMNS_MAP, MIXED_HIERARCHY_LIMIT } from '@/components/QuickSearch/constants'
import { CardSection, CategorySection, FamilyCardsSection, ListSection, SeeAllButton } from '@/components/QuickSearch/Parts'
import { NoResults } from '@/components/QuickSearch/Parts/NoResults'

interface AllCategoriesViewProps {
  query: string
  response: QuickSearchResponse
  onResultSelect: (result: QuickSearchResult) => void
  onSeeAll: (category: 'subdepartments' | 'families' | 'supermodels' | 'models') => void
}

export function AllCategoriesView({ query, response, onResultSelect, onSeeAll }: Readonly<AllCategoriesViewProps>) {
  const { t } = useTranslation()
  const [subdepartmentHiddenCount, setSubdepartmentHiddenCount] = useState<number | null>(null)
  const [familyHiddenCount, setFamilyHiddenCount] = useState<number | null>(null)
  const [supermodelHiddenCount, setSupermodelHiddenCount] = useState<number | null>(null)
  const [modelHiddenCount, setModelHiddenCount] = useState<number | null>(null)

  const familyItemCount = response.categories.families.results.filter(result => !!result.id).length
  const familyDisplayedCount = Math.max(0, familyItemCount - (familyHiddenCount ?? 0))
  const supermodelDisplayedCount = Math.max(0, response.categories.supermodels.results.length - (supermodelHiddenCount ?? 0))
  const modelDisplayedCount = Math.max(0, response.categories.models.results.length - (modelHiddenCount ?? 0))

  const hasAnyResults = Object.values(response.categories).some(cat => cat.total > 0)
  if (!hasAnyResults) {
    return <NoResults />
  }

  return (
    <div className="quick-search-results-page__content">
      {(response.categories.subdepartments.total > 0 || response.categories.families.total > 0) && (
        <CategorySection
          title={`${t('quick-search.hierarchy')} (${response.categories.subdepartments.total + response.categories.families.total})`}
          iconName="function-tiles"
        >
          {response.categories.subdepartments.total > 0 && (
            <div className="quick-search-results-page__section__wrapper">
              <div className="quick-search-results-page__section-header-wrapper">
                <h4 className="quick-search__section-subtitle">
                  {t('quick-search.category-subdepartments')}
                </h4>
                {subdepartmentHiddenCount !== null && subdepartmentHiddenCount > 0 && (
                  <SeeAllButton
                    category="subdepartments"
                    total={response.categories.subdepartments.total}
                    displayed={MIXED_HIERARCHY_LIMIT}
                    onSeeAll={onSeeAll}
                  />
                )}
              </div>
              <ListSection
                query={query}
                results={response.categories.subdepartments.results}
                displayMode="inline-pipe"
                enableOverflow
                onOverflowCountChange={setSubdepartmentHiddenCount}
                onResultSelect={onResultSelect}
              />
            </div>
          )}

          {response.categories.families.total > 0 && (
            <div className="quick-search-results-page__section__wrapper">
              <div className="quick-search-results-page__section-header-wrapper">
                <h4 className="quick-search__section-subtitle">
                  {t('quick-search.category-families')}
                </h4>
                <SeeAllButton
                  category="families"
                  total={response.categories.families.total}
                  displayed={familyDisplayedCount}
                  onSeeAll={onSeeAll}
                />
              </div>
              <FamilyCardsSection
                query={query}
                results={response.categories.families.results}
                enableOverflow
                onOverflowCountChange={setFamilyHiddenCount}
                onResultSelect={onResultSelect}
              />
            </div>
          )}
        </CategorySection>
      )}

      {response.categories.supermodels.total > 0 && (
        <CategorySection
          title={`Supermodels (${response.categories.supermodels.total})`}
          iconName="t-shirt-number"
          action={(
            <SeeAllButton
              category="supermodels"
              total={response.categories.supermodels.total}
              displayed={supermodelDisplayedCount}
              onSeeAll={onSeeAll}
            />
          )}
        >
          <CardSection
            query={query}
            results={response.categories.supermodels.results}
            onResultSelect={onResultSelect}
            gridColumns={GRID_COLUMNS_MAP.FULL_PAGE}
            enableOverflow
            onOverflowCountChange={setSupermodelHiddenCount}
          />
        </CategorySection>
      )}

      {response.categories.models.total > 0 && (
        <CategorySection
          title={`Models (${response.categories.models.total})`}
          iconName="t-shirt"
          action={(
            <SeeAllButton
              category="models"
              total={response.categories.models.total}
              displayed={modelDisplayedCount}
              onSeeAll={onSeeAll}
            />
          )}
        >
          <CardSection
            query={query}
            results={response.categories.models.results}
            onResultSelect={onResultSelect}
            gridColumns={GRID_COLUMNS_MAP.FULL_PAGE}
            enableOverflow
            onOverflowCountChange={setModelHiddenCount}
          />
        </CategorySection>
      )}
    </div>
  )
}
